import React, { useState } from 'react'
import Button from 'components/button'
import Input from 'components/input'
import Popover, { popoverPlacement } from 'components/popover'
import { PALETTE } from 'components/colour-picker'
import { SaveButtons } from 'components/save-buttons'
import { IContextMenuState } from 'types'

// About how tall the picker stands, footer and all.
const POPOVER_HEIGHT = 220

interface ColourFieldProps {
  /** What the colour is for: the trigger's accessible name, and the popover's heading. */
  label: string
  value: string
  /** Saves the colour picked; resolves true once it's saved, so the popover can close. */
  onSave: (colour: string) => Promise<boolean>
  /** The colour being picked, before it's saved, for a preview to follow; null once it's put away. */
  onDraft?: (colour: string | null) => void
  disabled?: boolean
  /**
   * Which edge of the swatch the picker lines up with: its end (the default, for a swatch at the end of
   * a row) or its start (for one at the start of a column).
   */
  align?: 'start' | 'end'
  /** Opened from inside another popover, which a click in the picker shouldn't close. */
  nested?: boolean
  /**
   * How the swatch shows: a compact button at the end of a settings row, or a full-width field
   * matching the text fields beside it in a popover.
   */
  variant?: 'button' | 'field'
  /**
   * Whether a colour waits for Save (the default), with Cancel beside it. Without, a swatch saves as
   * it's picked, and a hex code when Enter is pressed or the field is left, as every other setting in
   * a popover that saves as it changes does; the picker stays open to try another.
   */
  confirm?: boolean
}

/**
 * A colour setting, for any view: a swatch and its hex code, which opens a picker in a popover: the
 * palette's swatches, a hex field, and Cancel and Save on the popover's foot. Nothing changes until
 * Save, so a colour can be tried out (in a preview beside it) and put back. Clicking away or pressing
 * Escape cancels. (Or, with confirm off, it saves as it's picked; see below.)
 */
const ColourField: React.FC<ColourFieldProps> = ({
  label,
  value,
  onSave,
  onDraft,
  disabled,
  align = 'end',
  nested = false,
  variant = 'button',
  confirm = true
}) => {
  const [at, setAt] = useState<IContextMenuState | null>(null)
  const [draft, setDraft] = useState<string>(value)
  const [saving, setSaving] = useState<boolean>(false)

  const pick = (colour: string) => {
    setDraft(colour)
    onDraft?.(colour)
  }

  const close = () => {
    setAt(null)
    onDraft?.(null)
  }

  const canSave = draft.trim() !== '' && draft.trim().toLowerCase() !== value.toLowerCase()

  /** Saves the colour given, or else the one being picked. */
  const save = async (colour?: string) => {
    setSaving(true)
    const saved = await onSave(colour ?? draft.trim())
    setSaving(false)
    if (saved && confirm) close()
  }

  return (
    <>
      <Button
        internalType="outline"
        className={
          variant === 'field'
            ? 'h-8 w-full justify-start gap-2 rounded-md border-divider bg-field px-3 font-normal tabular-nums hover:border-faint hover:bg-field'
            : 'w-[108px] justify-start gap-2 px-2 font-normal tabular-nums'
        }
        aria-label={`${label}: ${value}`}
        aria-haspopup="dialog"
        aria-expanded={at !== null}
        disabled={disabled}
        onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
          // Below the swatch (above, near the foot of the window), lined up with the edge it's aligned to.
          const rect = event.currentTarget.getBoundingClientRect()
          setDraft(value)
          setAt(popoverPlacement(rect, { align, height: POPOVER_HEIGHT }))
        }}
      >
        <span className="size-4 shrink-0 rounded-[4px] border border-divider" style={{ backgroundColor: value }} />
        {value.toUpperCase()}
      </Button>
      <Popover
        id="colour-field"
        at={at}
        onClose={close}
        nested={nested}
        label={label}
        title={label}
        footer={confirm && <SaveButtons saving={saving} canSave={canSave} onCancel={close} onSave={() => save()} />}
      >
        <div className="grid grid-cols-8 gap-1.5">
          {PALETTE.map((swatch) => {
            const selected = draft.trim().toLowerCase() === swatch
            return (
              <Button
                key={swatch}
                internalType="transparent"
                aria-label={`Colour ${swatch}`}
                aria-pressed={selected}
                className={`aspect-square h-auto w-full min-w-0 rounded-md border border-black/10 p-0 hover:brightness-95 ${
                  selected ? 'ring-2 ring-blue ring-offset-1 ring-offset-surface-raised' : ''
                }`}
                style={{ backgroundColor: swatch }}
                onClick={() => {
                  pick(swatch)
                  if (!confirm && swatch !== value.toLowerCase()) save(swatch)
                }}
              />
            )
          })}
        </div>
        {/* The hex code, with the colour it makes inside the field, as an icon sits in a shadcn field. */}
        <div className="relative">
          <span
            className="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 rounded-[4px] border border-black/10"
            style={{ backgroundColor: draft }}
          />
          <Input
            aria-label="Hex code"
            className="h-8 rounded-md pl-8 tabular-nums"
            value={draft}
            onChange={(event) => pick(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter' && canSave) save()
            }}
            onBlur={() => {
              if (!confirm && canSave) save()
            }}
          />
        </div>
      </Popover>
    </>
  )
}

export default ColourField
